import { useState } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Save, Music, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ImageUploader from "./ImageUploader";


export default function MusicConfigTab({ config, onRefresh }) {
  const [form, setForm] = useState({
    youtube_url: config?.youtube_url || "",
    youtube_title: config?.youtube_title || "",
    spotify_url: config?.spotify_url || "",
    youtube_music_url: config?.youtube_music_url || "",
    apple_music_url: config?.apple_music_url || "",
    amazon_music_url: config?.amazon_music_url || "",
    banner_desktop: config?.banner_desktop || "",
    banner_mobile: config?.banner_mobile || "",
    page_title: config?.page_title || "MÚSICA",
    page_subtitle: config?.page_subtitle || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.put(`/music/config`, form);
      toast.success("Configuración guardada");
      onRefresh();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Music className="text-[#1DB954]" size={28} />
        <h2 className="text-xl font-bold text-black">Configuración de Página de Música</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        {/* YouTube Section */}
        <div className="p-4 bg-red-50 rounded-xl border border-red-200">
          <div className="flex items-center gap-2 mb-4">
            <Youtube className="text-red-600" size={20} />
            <span className="font-bold text-red-600">Video Destacado (YouTube)</span>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Título del Video</label>
              <Input
                value={form.youtube_title}
                onChange={(e) => setForm({...form, youtube_title: e.target.value})}
                placeholder="Nuevo video oficial"
                className="bg-white border-gray-200 text-black"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">URL del Video</label>
              <Input
                value={form.youtube_url}
                onChange={(e) => setForm({...form, youtube_url: e.target.value})}
                placeholder="https://youtube.com/watch?v=..."
                className="bg-white border-gray-200 text-black"
              />
              <p className="text-xs text-gray-400 mt-1">Pega el link del video de YouTube</p>
            </div>
          </div>
        </div>

        {/* Music Platforms Section */}
        <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
          <div className="flex items-center gap-2 mb-4">
            <Music className="text-purple-600" size={20} />
            <span className="font-bold text-purple-600">Plataformas de Música (iconos en la página)</span>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-600 mb-2 font-medium">
                <i className="fa-brands fa-spotify text-[#1DB954]"></i>
                Spotify
              </label>
              <Input
                value={form.spotify_url}
                onChange={(e) => setForm({...form, spotify_url: e.target.value})}
                placeholder="https://open.spotify.com/artist/..."
                className="bg-white border-gray-200 text-black"
              />
            </div>
            
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-600 mb-2 font-medium">
                <i className="fa-brands fa-youtube text-[#FF0000]"></i>
                YouTube Music
              </label>
              <Input
                value={form.youtube_music_url}
                onChange={(e) => setForm({...form, youtube_music_url: e.target.value})}
                placeholder="https://music.youtube.com/channel/..."
                className="bg-white border-gray-200 text-black"
              />
            </div>
            
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-600 mb-2 font-medium">
                <i className="fa-brands fa-apple text-gray-800"></i>
                Apple Music
              </label>
              <Input
                value={form.apple_music_url}
                onChange={(e) => setForm({...form, apple_music_url: e.target.value})}
                placeholder="https://music.apple.com/artist/..."
                className="bg-white border-gray-200 text-black"
              />
            </div>
            
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-600 mb-2 font-medium">
                <i className="fa-brands fa-amazon text-[#00A8E1]"></i>
                Amazon Music
              </label>
              <Input
                value={form.amazon_music_url}
                onChange={(e) => setForm({...form, amazon_music_url: e.target.value})}
                placeholder="https://music.amazon.com/artists/..."
                className="bg-white border-gray-200 text-black"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">Solo aparecerán los iconos de las plataformas con URL configurada</p>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-2 font-medium">Título de la Página</label>
          <Input
            value={form.page_title}
            onChange={(e) => setForm({...form, page_title: e.target.value})}
            placeholder="MÚSICA"
            className="bg-gray-50 border-gray-200 text-black"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-2 font-medium">Subtítulo (opcional)</label>
          <Input
            value={form.page_subtitle}
            onChange={(e) => setForm({...form, page_subtitle: e.target.value})}
            placeholder="La banda más chida del mundo"
            className="bg-gray-50 border-gray-200 text-black"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-2 font-medium">Banner Desktop</label>
          <ImageUploader
            value={form.banner_desktop}
            onChange={(url) => setForm({...form, banner_desktop: url})}
            category="banners"
            placeholder="URL del banner o sube una imagen"
            recommendedSize="Tamaño recomendado: 1920x350 píxeles"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-2 font-medium">Banner Móvil (opcional)</label>
          <ImageUploader
            value={form.banner_mobile}
            onChange={(url) => setForm({...form, banner_mobile: url})}
            category="banners"
            placeholder="URL del banner móvil"
            recommendedSize="Tamaño recomendado: 768x200 píxeles"
          />
        </div>

        <Button 
          type="submit" 
          disabled={saving}
          className="bg-[#1DB954] hover:bg-[#1DB954]/90 text-white"
        >
          <Save size={18} className="mr-2" />
          {saving ? "Guardando..." : "Guardar Configuración"}
        </Button>
      </form>
    </div>
  );
}
